import { PAGE_SIZE } from '@constants/api-constants';
import { PrivateURLConstants } from '@constants/private-url.constant';
import { TestCaseOverrideResult } from '@modules/test-development/models/test-case-override-result.model';
import { NOOP } from '@testsigma/atoms';
import { TestCaseResult, TestStep } from '@testsigmainc/models';

import { Page } from 'models/page';
import { useInfiniteQuery, useQuery } from 'react-query';
import api from '../api/instance';

const nameSpace = PrivateURLConstants.TEST_CASE_RESULTS;

type findAllProps = {
  query: string;
  sort?: string;
  size?: number;
  page?: number;
  args?: {
    [key: string]: any;
  };
};

type useTestCaseResultsFindAllProps = {
  payload: findAllProps;
  enabled: boolean;
  callback: (status, response) => void;
  beforeQuery?: () => void;
  key?;
};

const findAll = (
  query?: string,
  sort?: string,
  size?: number,
  page?: number,
  args?: {
    [key: string]: any;
  },
) => {
  return api
    .get(nameSpace, { query, sort, size, page, ...(args || {}) })
    .then((res) => new Page<TestCaseResult>().deserialize(res, TestCaseResult))
    .catch((e) => Promise.reject(e));
};

const findAllLegacy = (filterQuery?) => {
  return api
    .get(nameSpace + (filterQuery ? filterQuery : ''))
    .then((res) => {
      return new Page<TestCaseResult>().deserialize(res, TestCaseResult);
    })
    .catch((e) => Promise.reject(e));
};

const findAllPenultimate = (filterQuery?) => {
  return api
    .get(nameSpace + '/penultimate_results' + (filterQuery ? filterQuery : ''))
    .then((res) => {
      return new Page<TestCaseResult>().deserialize(res, TestCaseResult);
    })
    .catch((e) => Promise.reject(e));
};

const findById = (
  testCaseResultId: number,
  refresh?: boolean,
): Promise<TestCaseResult> => {
  return api
    .get(`${nameSpace}/${testCaseResultId}${refresh ? '?setPages=true' : ''}`)
    .then((res) => new TestCaseResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const isTestCaseDeleted = (testCaseResultId: number) => {
  return findById(testCaseResultId).then((testCaseResult: TestCaseResult) => {
    return !testCaseResult?.testCase?.id;
  });
};

const assetSync = (testCaseResultId: number) => {
  const url = nameSpace + '/' + testCaseResultId + '/assets/sync';

  return api
    .get(url)
    .then()
    .catch((e) => Promise.reject(e));
};

const assetStatus = (testCaseResultId: number): Promise<string> => {
  const url = nameSpace + '/' + testCaseResultId + '/assets/status';

  return api
    .get(url)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const overrideResult = (payload: TestCaseOverrideResult) => {
  return api
    .put(
      `${nameSpace}/${payload.testCaseResultId}/override`,
      payload.serialize(),
    )
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

const useTestCaseResultFindAll = ({
  payload,
  key = null,
  callback,
  beforeQuery = NOOP,
}: useTestCaseResultsFindAllProps) => {
  return useQuery({
    queryKey: key || payload,
    queryFn: () => {
      beforeQuery();
      return findAll(
        payload.query,
        payload.sort,
        payload.size,
        payload.page,
        payload.args,
      );
    },
    onSuccess: (data) => callback(true, data),
    onError: (err) => callback(false, err),
  });
};

export const useTestCaseResultFindAllInfinite = ({
  query,
  args = {},
  enabled,
  refetchOnMount = true,
  callback = (_, __) => {},
  queryCacheKey = [],
}) => {
  return useInfiniteQuery({
    queryKey: [
      ...queryCacheKey,
      `use-test-case-result-find-all-infinite-${query}`,
    ].filter(Boolean),
    queryFn: async ({ pageParam = 0 }) => {
      const response = await findAll(query, null, PAGE_SIZE, pageParam, args);
      if (callback) {
        callback(response?.content, response?.totalElements);
      }
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.last ? undefined : lastPage?.pageable?.pageNumber + 1;
    },
    enabled,
    refetchOnMount,
  });
};

const updateDebugPoints = (testCaseResult: TestCaseResult) => {
  return api
    .put(`${nameSpace}/update_debug_points`, testCaseResult.serialize())
    .then((res) => new TestCaseResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const updateRerun = (testCaseResult: TestCaseResult) => {
  return api
    .put(`${nameSpace}/update_rerun`, testCaseResult.serialize())
    .then((res) => new TestCaseResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const update = (testCaseResult: TestCaseResult) => {
  return api
    .put(`${nameSpace}/${testCaseResult.id}`, testCaseResult.serialize())
    .then((res) => new TestCaseResult().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const fetchTestsigmaLogs = (runId: number, testCaseResultId: number) => {
  return api
    .get(`${nameSpace}/log_url/${runId}/${testCaseResultId}`)
    .then((res) => res)
    .catch((err) => Promise.reject(err));
};

const markAsFixed = (
  testCaseResultId: number,
  updatedTestStep: TestStep | null,
) => {
  return api
    .put(`${nameSpace}/${testCaseResultId}/mark_as_fixed`, updatedTestStep)
    .then((res) => res)
    .catch((e) => Promise.reject(e));
};

export const TestCaseResultService = {
  findAll,
  findAllLegacy,
  findAllPenultimate,
  findById,
  assetSync,
  assetStatus,
  overrideResult,
  updateDebugPoints,
  updateRerun,
  update,
  fetchTestsigmaLogs,
  isTestCaseDeleted,
  markAsFixed,
};
